const BASE = '/api/v1';
const BASE_LONG = '/api/long';

async function req(path: string, opts: RequestInit = {}) {
  const res = await fetch(BASE + path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
    ...opts,
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = await res.json();
      if (body.error) msg = body.error;
    } catch {}
    const err: any = new Error(msg);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  return res.text();
}

// 장시간 API (LLM 분석 등) — /api/long prefix, 서버 Timeout 미들웨어 없음.
async function reqLong(path: string, opts: RequestInit = {}) {
  const res = await fetch(BASE_LONG + path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
    ...opts,
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = await res.json();
      if (body.error) msg = body.error;
    } catch {}
    const err: any = new Error(msg);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  return res.text();
}

export const api = {
  me: () => req('/me'),
  login: (email: string, password: string) =>
    req('/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => req('/logout', { method: 'POST' }),

  stats: () => req('/dashboard/stats'),
  products: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params)
          .filter(([, v]) => v !== '' && v !== undefined && v !== null)
          .map(([k, v]) => [k, String(v)]),
      ),
    ).toString();
    return req('/products' + (qs ? '?' + qs : ''));
  },
  product: (id: number | string) => req(`/products/${id}`),
  productsAction: (
    ids: number[],
    action:
      | 'halt' | 'activate'          // 판매+전시 동시
      | 'suspend' | 'resume'
      | 'display_off' | 'display_on'
      | 'sold_out' | 'restock',
  ) => req('/products-action', { method: 'POST', body: JSON.stringify({ ids, action }) }),
  deleteProducts: (ids: number[]) =>
    req('/delete-products', { method: 'POST', body: JSON.stringify({ ids }) }),

  jobs: (limit = 50) => req(`/jobs?limit=${limit}`),
  createJob: (type: string, payload: Record<string, unknown> = {}) =>
    req('/jobs', { method: 'POST', body: JSON.stringify({ type, payload }) }),
  cancelJob: (id: number) =>
    req('/jobs', { method: 'POST', body: JSON.stringify({ type: 'cancel', payload: { id } }) }),
  retryJob: (id: number) =>
    req('/jobs', { method: 'POST', body: JSON.stringify({ type: 'retry', payload: { id } }) }),

  logs: (limit = 100, jobID?: number) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (jobID) qs.set('job_id', String(jobID));
    return req(`/logs?${qs.toString()}`);
  },

  providers: () => req('/providers'),

  providerCreds: () => req('/settings/provider-credentials'),
  saveProviderCred: (data: {
    provider: string;
    label: string;
    username: string;
    password: string;
  }) =>
    req('/settings/provider-credentials', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteProviderCred: (id: number) =>
    req(`/settings/provider-credentials/${id}`, { method: 'DELETE' }),

  naverAPI: () => req('/settings/naver-api'),
  saveNaverAPI: (data: { store_name: string; client_id: string; client_secret: string }) =>
    req('/settings/naver-api', { method: 'POST', body: JSON.stringify(data) }),

  // llm / telegram settings moved to the unified plane at
  // /api/admin/settings/{llm,telegram}. The Settings page renders them via
  // the saas-common SettingsPage; this client no longer needs dedicated
  // methods. Only the test-send action remains here because the common
  // plane doesn't have an equivalent.
  testTelegram: () =>
    req('/settings/telegram/test', { method: 'POST' }),

  // 이미지 합성 검수
  productCategories: () => req('/products/categories'),
  composites: (status: string) => req(`/composites?status=${encodeURIComponent(status)}`),
  compositePresets: () => req('/composites/presets'),
  compositeImageURL: (id: number) => `/api/v1/composites/${id}/image`,
  reviewComposites: (data: { ids: number[]; action: 'approve' | 'reject' }) =>
    req('/composites/review', { method: 'POST', body: JSON.stringify(data) }),

  schedule: () => req('/settings/schedule'),
  saveSchedule: (data: {
    crawl_cron: string;
    register_cron: string;
    naver_pull_cron: string;
    supplier_soldout_cron: string;
    product_state_cron?: string;
    price_change_pct: number;
    margin_rate: number;
    enabled: boolean;
    reprice_cron?: string;
    reprice_dry_run?: boolean;
    reprice_undercut_pct?: number;
    reprice_min_margin_rate?: number;
    reprice_assumed_shipping_fee?: number;
    reprice_match_threshold?: number;
    expose_floor_cron?: string;
    expose_floor_dry_run?: boolean;
    expose_floor_max_raise_rate?: number;
    margin_floor_target_rate?: number;
    margin_floor_supplier_ship?: number;
    margin_floor_collected_ship?: number;
    margin_floor_fee_rate?: number;
  }) =>
    req('/settings/schedule', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Reprice — naver shopping search API credentials (separate from commerce API).
  naverSearchCreds: () => req('/settings/naver-search'),
  saveNaverSearchCreds: (data: {
    client_id: string;
    client_secret: string; // empty = keep existing
    enabled: boolean;
  }) =>
    req('/settings/naver-search', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Reprice — recent job runs + per-product observations.
  repriceRuns: (limit = 20) => req(`/reprice/runs?limit=${limit}`),
  // Reprice observations — by product OR by job (backend requires at least one).
  repriceObservations: (params: { product_id?: number; job_id?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params.product_id) qs.set('product_id', String(params.product_id));
    if (params.job_id) qs.set('job_id', String(params.job_id));
    qs.set('limit', String(params.limit ?? 200));
    return req(`/reprice/observations?${qs.toString()}`);
  },
  repricePreview: (productId: number) =>
    req('/reprice/preview', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId }),
    }),

  bulkAdjustPreview: (body: {
    mode: 'margin' | 'pct' | 'flat' | 'expose';
    value: number;
    scope: 'all' | 'margin_only' | 'product_ids';
    product_ids?: number[];
  }) =>
    req('/reprice/bulk-preview', { method: 'POST', body: JSON.stringify(body) }),

  orders: (limit = 100) => req(`/orders?limit=${limit}`),
  income: (days = 7) => req(`/income?days=${days}`),

  analyzeProduct: (id: number) =>
    reqLong(`/analyze/${id}`, { method: 'POST' }),

  appInfo: () => req('/app-info'),
};
